import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { Edit } from "@mui/icons-material"; // Removed Delete and Add icons
import axios from "axios";
import { useSnackbar } from "../../../Providers/SnackbarProvider";
import { useNavigate } from "react-router-dom";
import useSharedLink from "../../../../src/SharedLink";
import axiosInstance from "../../../api/axiosInstance";

const ManageSubscriber = () => {
  const sharedLink = useSharedLink();
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false); // State for History Dialog
  const [historyRows, setHistoryRows] = useState([]); // State to hold history rows
  const [isEditMode, setIsEditMode] = useState(false);
  const [subscriberDetails, setSubscriberDetails] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showInactive, setShowInactive] = useState(false); // State to manage checkbox
  const [showNotEnrolled, setShowNotEnrolled] = useState(false); // State to manage not enrolled checkbox
 const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [snackbarDuration, setSnackbarDuration] = useState(3000); // Default duration is 3 seconds

  // --- Added new state for the verification logic ---
  const [timer, setTimer] = useState(0); // Timer for resend
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // To disable button during countdown
  const [progress, setProgress] = useState(100); // Progress for LinearProgress bar

  

  const fetchAllSubscribers = () => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getallsubscribers`,
        {},
        { withCredentials: true }
      )
      .then((response) => {
        setRows(response.data);
        setAllRows(response.data);
      })
      .catch((error) => {
        openSnackbar("Error fetching subscribers:", 'error');
      });
  };

  useEffect(() => {
    fetchAllSubscribers();
  }, []);

  const handleEdit = (id) => {
    axiosInstance
      .post(
        `${sharedLink}/api/abanotes/getsubscriber`,
        { id: id },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      )
      .then((response) => {
        const subscriber = response.data[0];
        const currentDate = new Date().toISOString();
        const {
          id, // Ensure the ID is included here
          name,
          email,
          dateOfBirth,
          phoneNumber,
          gender,
          address,
          role,
          password,
          securityAnswer1,
          securityAnswer2,
          securityQuestion1,
          securityQuestion2,
          buildingNumber,
          streetName,
          city,
          state,
          country,
          pincode,
          enrollments, // Ensure enrollments are included
        } = subscriber;

        const enrollmentStatus =
          enrollments && enrollments.length > 0
            ? enrollments[0].enrollmentStatus
            : "Inactive"; // Default to Inactive if no enrollments
        // Convert the dateOfBirth to 'YYYY-MM-DD' format
        const formattedDateOfBirth = new Date(dateOfBirth)
          .toISOString()
          .split("T")[0];

        setSubscriberDetails({
          id, // Set the ID in the subscriber details
          name,
          email,
          dateOfBirth: formattedDateOfBirth, // Set formatted date without time
          phoneNumber,
          gender,
          address,
          role,
          lastUpdatedBy: "admin",
          lastUpdatedTime: currentDate,
          enteredTime: currentDate,
          enteredBy: "admin",
          password,
          securityAnswer1,
          securityAnswer2,
          securityQuestion1,
          securityQuestion2,
          buildingNumber,
          streetName,
          city,
          state,
          country,
          pincode,
          enrollments, // Set enrollments in the state
          enrollmentStatus, // Add enrollmentStatus to the state
        });

        setIsEditMode(true);
        setOpenEdit(true);
      })
      .catch((error) => {
        openSnackbar("Error fetching subscriber details:", 'error');
      });
  };

  const handleClose = () => {
    setOpenEdit(false);
    setOpenAdd(false);
    setOpenHistoryDialog(false); // Close History Dialog
    setSubscriberDetails({});
    setIsEditMode(false);
  };

  // --- New function to start the 60-second countdown and show Snackbar ---
  const startTimer = () => {
    setIsButtonDisabled(true); // Disable button for 60 seconds
    setTimer(60); // Set timer to 60 seconds
    setProgress(100); // Reset progress bar

    // Show Snackbar with verification link message
    openSnackbar(
      <>
        Verification link has been sent to your email.
        <br />
        If you have not received it, please check your email and try again in 60
        seconds.
      </>
    );
    setSnackbarSeverity("success"); // Set success message
    setSnackbarDuration(10000); // Set duration to 10 seconds for verification
    setSnackbarOpen(true); // Open the Snackbar

    // Start a countdown
    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown); // Stop the timer at 0
          setIsButtonDisabled(false); // Re-enable the button
          setSnackbarOpen(false); // Close the Snackbar
          return 0;
        }
        setProgress((prevProgress) => prevProgress - 100 / 60); // Decrease progress
        return prev - 1;
      });
    }, 1000); // Run every second
  };

  const updateSubscriber = () => {
    if (!subscriberDetails.email) {
      openSnackbar("Email is required",'info');
      
      return;
    } else if (!subscriberDetails.email.includes("@")) {
      openSnackbar("Please provide a valid email address",'info');
      return;
    }

    // Additional validation for Date of Birth
    const birthDate = new Date(subscriberDetails.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    // Display error if age is less than 18
    if (isNaN(age) || age < 18) {
      setDateOfBirthError("You must be at least 18 years old");
      openSnackbar(
        "Please enter a valid date of birth. You must be at least 18 years old.",'info'
      );
      return; // Prevent submission
    }

    // Check if dateOfBirth is valid
    if (!subscriberDetails.dateOfBirth || isNaN(birthDate.getTime())) {
      setDateOfBirthError("Please enter a valid date of birth",'info');
      setSnackbarOpen(true);
      return;
    }

    // Validate Security Questions
    if (!subscriberDetails.securityAnswer1) {
      openSnackbar("Security question 1 is required",'info');
      return; // Prevent submission
    }

    if (!subscriberDetails.securityAnswer2) {
      openSnackbar("Security question 2 is required",'info');
      return; // Prevent submission
    }
    // --- Password Validation for Edit ---
    if (!subscriberDetails.password) {
      openSnackbar("Password is required.", 'info');
      return;
    }

    if (window.confirm("Are you sure you want to update?")) {
      const payload = {
        id: subscriberDetails.id, // Ensure the ID is included in the payload
        name: subscriberDetails.name,
        email: subscriberDetails.email,
        dateOfBirth: subscriberDetails.dateOfBirth,
        phoneNumber: subscriberDetails.phoneNumber,
        gender: subscriberDetails.gender,
        address: subscriberDetails.address,
        role: subscriberDetails.role,
        lastUpdatedBy: "admin",
        lastUpdatedTime: new Date().toISOString(),
        enteredBy: subscriberDetails.enteredBy,
        enteredTime: subscriberDetails.enteredTime,
        password: subscriberDetails.password,
        securityAnswer1: subscriberDetails.securityAnswer1,
        securityAnswer2: subscriberDetails.securityAnswer2,
        securityQuestion1: subscriberDetails.securityQuestion1,
        securityQuestion2: subscriberDetails.securityQuestion2,
        buildingNumber: subscriberDetails.buildingNumber,
        streetName: subscriberDetails.streetName,
        city: subscriberDetails.city,
        state: subscriberDetails.state,
        country: subscriberDetails.country,
        pincode: subscriberDetails.pincode,
        enrollments: subscriberDetails.enrollments.map((enrollment) => ({
          ...enrollment, // Include all fields of the enrollment object
          enrollmentStatus: subscriberDetails.enrollmentStatus,
        })),
      };

      axiosInstance
        .post(`${sharedLink}/api/abanotes/updatesubscriber`, payload, {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        })
        .then((response) => {
          //console.log("Update successful:", response.data);
          setOpenEdit(false);
          // Show success Snackbar after the update is successful
          openSnackbar("Subscriber updated successfully",'success');
          fetchAllSubscribers();
        })
        .catch((error) => {
          openSnackbar("Error updating subscriber:", 'error');
          setSnackbarOpen(true);
        });
    }
  };

  const addSubscriber = async (e) => {
    e.preventDefault();

    // Additional validation for Date of Birth
    const birthDate = new Date(subscriberDetails.dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();

    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    // Display error if age is less than 18
    if (isNaN(age) || age < 18) {
      setDateOfBirthError("You must be at least 18 years old");
      openSnackbar(
        "Please enter a valid date of birth. You must be at least 18 years old.",'info'
      );
      return; // Prevent submission
    }

    // Check if dateOfBirth is valid
    if (!subscriberDetails.dateOfBirth || isNaN(birthDate.getTime())) {
      setDateOfBirthError("Please enter a valid date of birth");
      openSnackbar("Please enter a valid date of birth.",'info');
      return;
    }

    // Validation logic for required fields
    if (!subscriberDetails.name) {
      openSnackbar("Name is required",'info');
    }

    if (!subscriberDetails.email) {
      openSnackbar("Email is required",'info');
      return; // Prevent submission if validation fails
    } else if (!subscriberDetails.email.includes("@")) {
      openSnackbar("Please provide a valid email address",'info');
      return; // Prevent submission if email format is invalid
    }

    if (!subscriberDetails.phoneNumber) {
      openSnackbar("Phone number is required",'info');
      return; // Prevent submission if validation fails
    }

    // Validation logic for required fields
    if (!subscriberDetails.securityAnswer1) {
      openSnackbar("Security question 1 is required",'info');
      return; // Prevent form submission
    }

    if (!subscriberDetails.securityAnswer2) {
      openSnackbar("Security question 2 is required",'info');
      return; // Prevent form submission
    }

    // --- Password Validation for Add ---
    if (!subscriberDetails.password) {
      openSnackbar("Password is required.",'info');
      return;
    }

    // Confirmation alert before proceeding with submission
    const confirmation = window.confirm(
      "Are you sure you want to add this subscriber?"
    );

    if (!confirmation) {
      // If user cancels, do not proceed with the submission
      return;
    }

    // if (!subscriberDetails.dateOfBirth) {
    //   openSnackbar("Date of Birth is required");
    //   setSnackbarSeverity("error");
    //   setSnackbarOpen(true);
    //   return; // Prevent submission if validation fails
    // }

    const currentDate = new Date().toISOString();
    const {
      name,
      email,
      dateOfBirth,
      phoneNumber,
      gender,
      address,
      role,
      password,
      securityAnswer1,
      securityAnswer2,
      securityQuestion1,
      securityQuestion2,
      buildingNumber,
      streetName,
      city,
      state,
      country,
      pincode,
      // enrollmentStatus, // Include enrollmentStatus in the payload
      // enrollmentType, // Add enrollmentType in the payload
      // customerId, // Add customerId in the payload
      // sessionId, // Add sessionId in the payload
    } = subscriberDetails;

    // Ensure the dateOfBirth is a valid date before converting to ISO string
    let validDateOfBirth = "";
    if (dateOfBirth) {
      validDateOfBirth = new Date(dateOfBirth).toISOString();
    }

    const payload = {
      name,
      email,
      dateOfBirth: validDateOfBirth,
      phoneNumber,
      gender,
      address,
      role,
      lastUpdatedBy: "admin",
      lastUpdatedTime: currentDate,
      enteredTime: currentDate,
      enteredBy: "admin",
      password,
      securityAnswer1,
      securityAnswer2,
      securityQuestion1,
      securityQuestion2,
      buildingNumber,
      streetName,
      city,
      state,
      country,
      pincode,
      // enrollments: [
      //   {
      //     productId: null, // Adjust this field as necessary
      //     productPlan: null, // Adjust this field as necessary
      //     productSubscriptionType: null, // Adjust this field as necessary
      //     enrollmentType: enrollmentType || "DefaultType", // Ensure enrollmentType is not null
      //     enrollmentStatus: enrollmentStatus || "Inactive",
      //     customerId: customerId || "DefaultCustomerID", // Ensure customerId is not null
      //     sessionId: sessionId || "DefaultSessionID", // Ensure sessionId is not null
      //     enrollmentStartDate: currentDate,
      //     enrollmentEndDate: currentDate,
      //     enteredBy: "admin",
      //     enteredTime: currentDate,
      //     lastUpdatedBy: "admin",
      //     lastUpdatedTime: currentDate,
      //   },
      // ],
    };

    try {
      const res = await axiosInstance.post(
        `${sharedLink}/api/abanotes/addsubscriber`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );

      if (res && res.data) {
        //console.log("Add successful:", res.data);
        openSnackbar(
          res.data.message || "Subscriber added successfully!"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setOpenAdd(false);
        fetchAllSubscribers();

        // --- Start the timer after adding a subscriber ---
        startTimer(); // Start 60-second timer and show verification link message
      } else {
        openSnackbar(res.data || "Error adding subscriber");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      openSnackbar("Error adding subscriber:", error);
      openSnackbar(error.response?.data || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleAdd = () => {
    setSubscriberDetails({
      name: "",
      email: "",
      dateOfBirth: "",
      phoneNumber: "",
      gender: "",
      address: "",
      role: "",
      password: "",
      securityAnswer1: "",
      securityAnswer2: "",
      securityQuestion1: "Your first college name?",
      securityQuestion2: "Your favourite sport?",
      buildingNumber: "",
      streetName: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      // enrollments: [], // Initialize enrollments as an empty array
      // enrollmentStatus: "Inactive", // Default enrollment status to Inactive
    });
    setIsEditMode(false);
    setOpenAdd(true);
  };

  const handleShowInactiveChange = (event) => {
    setShowInactive(event.target.checked);
    if (event.target.checked) {
      setShowNotEnrolled(false);
    }
  };

  const [dateOfBirthError, setDateOfBirthError] = useState("");

  useEffect(() => {
    fetchAllSubscribers();
  }, []);

  const handleShowNotEnrolledChange = (event) => {
    setShowNotEnrolled(event.target.checked);

    // Show snackbar notification if "Show Not Enrolled Subscribers" is checked
    if (event.target.checked) {
      setShowInactive(false);
      openSnackbar("These Subscribers have not enrolled yet");
    }
  };

  useEffect(() => {
    if (allRows.length > 0) {
      const filteredRows = allRows
        .map((row) => {
          const enrollmentStatus =
            row.enrollments && row.enrollments.length > 0
              ? row.enrollments[0].enrollmentStatus
              : row.enrollmentStatus; // Set to "Inactive" for empty enrollments
          return {
            ...row,
            enrollmentStatus: enrollmentStatus,
          };
        })
        .filter((row) => {
          if (showNotEnrolled) {
            // If "Show Not Enrolled Subscribers" is checked, only show subscribers with enrollments: []
            return row.enrollments.length === 0;
          } else if (showInactive) {
            // Show subscribers with "Inactive" enrollment status and non-empty enrollments
            return (
              row.enrollmentStatus === "Inactive" && row.enrollments.length > 0
            );
          } else {
            // Default: Show only subscribers with "Active" enrollment status
            return row.enrollmentStatus === "Active";
          }
        });
      setRows(filteredRows);
    }
  }, [showInactive, showNotEnrolled, allRows]);

  const fetchEnrollmentDetails = async (email) => {
    try {
      const response = await axiosInstance.post(
        `${sharedLink}/api/abanotes/getenrollment`,
        { email },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error) {
      openSnackbar("Error fetching enrollment details:", error);
      return [];
    }
  };

  const toggleEnrollmentStatus = (id, newStatus) => {
    const updatedSubscriber = allRows.find((row) => row.id === id);
    if (updatedSubscriber && updatedSubscriber.enrollments[0]) {
      const updatedEnrollment = {
        ...updatedSubscriber.enrollments[0],
        enrollmentStatus: newStatus,
      };

      axiosInstance
        .post(
          `${sharedLink}/api/abanotes/updatesubscriber`,
          {
            ...updatedSubscriber,
            enrollments: [updatedEnrollment],
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        )
        .then((response) => {
          //console.log("Update successful:", response.data);

          fetchAllSubscribers(); // Refresh the subscriber list
        })
        .catch((error) => {
          openSnackbar("Error updating subscriber:", error);
        });
    }
  };

  const columns = [
    { field: "name", headerName: "Name", width: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "phoneNumber", headerName: "Phone Number", width: 150 },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      width: 150,
      valueGetter: (params) =>
        new Date(params.value).toISOString().split("T")[0],
    },
    {
      field: "enrollmentStatus",
      headerName: "Enrollment Status",
      width: 150,
      valueGetter: (params) => {
        const enrollments = params.row.enrollments || [];
        if (enrollments.length === 0) return "Inactive";
        const sortedEnrollments = enrollments.sort(
          (a, b) => new Date(b.enrollmentStartDate) - new Date(a.enrollmentStartDate)
        );
        return sortedEnrollments[0].enrollmentStatus || "Inactive";
      },
    },
    {
      field: "enrollmentStartDate",
      headerName: "Enrollment Start Date",
      width: 200,
      valueGetter: (params) =>
        params.row.enrollments && params.row.enrollments[0]
          ? new Date(params.row.enrollments[0].enrollmentStartDate)
              .toISOString()
              .split("T")[0]
          : "",
    },
    {
      field: "enrollmentEndDate",
      headerName: "Enrollment End Date",
      width: 200,
      valueGetter: (params) =>
        params.row.enrollments && params.row.enrollments[0]
          ? new Date(params.row.enrollments[0].enrollmentEndDate)
              .toISOString()
              .split("T")[0]
          : "",
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 150,
      getActions: (params) => {
        const enrollmentStatus =
          params.row.enrollments && params.row.enrollments.length > 0
            ? params.row.enrollments[0].enrollmentStatus
            : "Inactive"; // Use "Inactive" for empty enrollments

        // Check if showNotEnrolled is true and enrollments are empty
        if (showNotEnrolled && params.row.enrollments.length === 0) {
          return [
            <GridActionsCellItem
              key={params.id}
              icon={<Edit />}
              label="Edit"
              onClick={() => handleEdit(params.id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem
            key={params.id}
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(params.id)}
          />,
          // Remove Delete and Add icons
        ];
      },
    },
  ];

  // Function to handle viewing history
  const handleViewHistory = (email) => {
    fetchEnrollmentDetails(email).then((data) => {
      const historyData = data.map((enrollment, index) => ({
        id: index + 1,
        productId: enrollment.productId,
        enrollmentStatus: enrollment.enrollmentStatus,
        enrollmentStartDate: new Date(enrollment.enrollmentStartDate)
          .toISOString()
          .split("T")[0],
        enrollmentEndDate: new Date(enrollment.enrollmentEndDate)
          .toISOString()
          .split("T")[0],
      }));
      setHistoryRows(historyData);
      setOpenHistoryDialog(true);
    });
  };

  const historyColumns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "productId", headerName: "Product ID", width: 150 },
    { field: "enrollmentStatus", headerName: "Enrollment Status", width: 150 },
    { field: "enrollmentStartDate", headerName: "Start Date", width: 200 },
    { field: "enrollmentEndDate", headerName: "End Date", width: 200 },
  ];

  return (
    <Box
      sx={{
        width: "80%",
        mt: 20,
        ml: "auto",
        mr: "auto",
        backdropFilter: "blur(10px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{ mb: 10, textAlign: "center", fontWeight: "bold", fontSize: 35 }}
        >
          Manage Subscribers
        </Typography>
        {/* <Button
          variant="contained"
          onClick={handleAdd}
          sx={{
            mt: -9,
            backgroundColor: "#e9a132",
            "&:hover": { backgroundColor: "#c78926" },
          }}
        >
          Add Subscriber
        </Button> */}
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={showInactive}
            onChange={handleShowInactiveChange}
            color="primary"
          />
        }
        label="Show Inactive Enrollment"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={showNotEnrolled}
            onChange={handleShowNotEnrolledChange}
            color="primary"
          />
        }
        label="Show Not Enrolled Subscribers"
      />

      <Box sx={{ height: "calc(90vh - 260px)", width: "100%" }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          pageSize={5}
          getRowId={(row) => row.id}
          className="animated-input"
          sx={{
            boxShadow: 5,
            border: "none",
            "& .MuiDataGrid-cell:hover": {
              color: "primary.main",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white !important",
              fontWeight: "bold !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#e9a132 !important",
              borderBottom: "white !important",
              alignContent: "center",
              alignItems: "center",
              display: "flex",
              height: "100%",
            },
            "& .MuiDataGrid-columnSeparator": {
              visibility: "hidden",
            },
            "& .MuiDataGrid-toolbarContainer": {
              color: "white !important",
              justifyContent: "space-between",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              color: "white !important",
              fontWeight: "bold !important",
              backgroundColor: "#e9a132 !important",
            },
            "& .MuiTablePagination-root": {
              color: "white !important",
            },
            "& .MuiIconButton-root": {
              color: "black !important",
            },
            "& .MuiDataGrid-selectedRowCount": {
              color: "white !important",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 20px",
          backgroundColor: "white",
          color: "black",
          borderTop: "1px solid #fff",
          borderColor: "yellow",
        }}
      ></Box>

      {/* Edit Subscriber Dialog */}

      <Dialog open={openEdit} onClose={handleClose}>
        <DialogTitle>Edit Subscriber Details</DialogTitle>
        <DialogContent>
          <TextField
            label={
              <span>
                Name<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.name || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            onChange={(e) => {
              const regex = /^[A-Za-z\s]*$/; // Only allow alphabets and spaces
              if (regex.test(e.target.value)) {
                setSubscriberDetails({
                  ...subscriberDetails,
                  name: e.target.value,
                });
              }
            }}
          />
          <TextField
            label={
              <span>
                Email<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.email || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                email: e.target.value,
              })
            }
          />

          <TextField
            label={
              <span>
                Phone<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.phoneNumber || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            inputProps={{ maxLength: 10 }} // Limit input length to 10 characters
            onChange={(e) => {
              const regex = /^[0-9]*$/; // Only allow digits
              if (regex.test(e.target.value)) {
                setSubscriberDetails({
                  ...subscriberDetails,
                  phoneNumber: e.target.value,
                });
              }
            }}
          />
          <TextField
            label={
              <span>
                Date of Birth<span style={{ color: "red" }}> *</span>
              </span>
            }
            type="date"
            value={subscriberDetails.dateOfBirth || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            error={Boolean(dateOfBirthError)} // Display error if any
            helperText={dateOfBirthError} // Show error message
            onChange={(e) => {
              const inputDate = e.target.value;
              setSubscriberDetails({
                ...subscriberDetails,
                dateOfBirth: inputDate,
              });
            }}
            onBlur={() => {
              const birthDate = new Date(subscriberDetails.dateOfBirth);

              // Validate if the date is a valid date
              if (isNaN(birthDate.getTime())) {
                setDateOfBirthError("Please enter a valid date");
                openSnackbar("Please enter a valid date of birth.",'info');
                return;
              }

              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDifference = today.getMonth() - birthDate.getMonth();

              if (
                monthDifference < 0 ||
                (monthDifference === 0 && today.getDate() < birthDate.getDate())
              ) {
                age--;
              }

              // Show error if user is under 18
              if (age < 18) {
                setDateOfBirthError("You must be at least 18 years old");
                openSnackbar("You must be at least 18 years old.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
              } else {
                setDateOfBirthError("");
              }
            }}
            InputLabelProps={{
              shrink: true, // Ensures the label stays in place
            }}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel>Gender</InputLabel>
            <Select
              value={subscriberDetails.gender || ""}
              onChange={(e) =>
                setSubscriberDetails({
                  ...subscriberDetails,
                  gender: e.target.value,
                })
              }
              variant="outlined"
              label="Gender"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Address"
            value={subscriberDetails.address || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                address: e.target.value,
              })
            }
          />
          <TextField
            label="Role"
            value={subscriberDetails.role || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                role: e.target.value,
              })
            }
          />

          <TextField
            label="Street Name"
            value={subscriberDetails.streetName || ""}  
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                streetName: e.target.value,
              })
            }
          />
          <TextField
            label="City"
            value={subscriberDetails.city || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                city: e.target.value,
              })
            }
          />
          <TextField
            label="State"
            value={subscriberDetails.state || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                state: e.target.value,
              })
            }
          />
          <TextField
            label="Country"
            value={subscriberDetails.country || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                country: e.target.value,
              })
            }
          />
          <TextField
            label="Pincode"
            value={subscriberDetails.pincode || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                pincode: e.target.value,
              })
            }
          />
          {/* Security Questions */}
          <Typography gutterBottom sx={{ mt: 1 }}>
            Security Questions<span style={{ color: "red" }}> *</span>
          </Typography>

          {/* Input for Security Answer 1 */}
          <TextField
            label={
              <span>
                Your first college name?<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.securityAnswer1 || ""} // Bind to securityAnswer1
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer1: e.target.value, // Update the correct state field
              })
            }
          />

          {/* Input for Security Answer 2 */}
          
          <TextField
            label={
              <span>
                Your favorite sport?<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.securityAnswer2 || ""} // Bind to securityAnswer2
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer2: e.target.value, // Update the correct state field
              })
            }
          />

          <TextField
            label="Password"
            value={subscriberDetails.password || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                password: e.target.value,
              })
            }
          />
          {/* <TextField
            label="Security Answer 1"
            value={subscriberDetails.securityAnswer1 || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer1: e.target.value,
              })
            }
          />
          <TextField
            label="Security Answer 2"
            value={subscriberDetails.securityAnswer2 || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer2: e.target.value,
              })
            }
          /> */}
          {/* Enrollment Status Dropdown */}
          {/* <FormControl fullWidth margin="dense">
            <InputLabel>Enrollment Status</InputLabel>
            <Select
              value={subscriberDetails.enrollmentStatus || "Inactive"}
              onChange={(e) =>
                setSubscriberDetails({
                  ...subscriberDetails,
                  enrollmentStatus: e.target.value,
                })
              }
              variant="outlined"
              label="Enrollment Status"
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl> */}
          {/* View History Button */}
          {subscriberDetails.enrollments &&
            subscriberDetails.enrollments.length > 0 && (
              <Button
                onClick={() => handleViewHistory(subscriberDetails.email)} // Pass email to fetch history
                color="primary"
                variant="outlined"
                sx={{ mt: 2 }}
              >
                View Enrollment History
              </Button>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            onClick={updateSubscriber}
            color="primary"
            variant="contained"
            sx={{
              backgroundColor: "#e9a132",
              "&:hover": { backgroundColor: "#c78926" },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>



      {/* Add Subscriber Dialog */}
      <Dialog open={openAdd} onClose={handleClose}>
        <DialogTitle>Add Subscriber Details</DialogTitle>
        <DialogContent>
          <TextField
            label={
              <span>
                Name<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.name || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            onChange={(e) => {
              const regex = /^[A-Za-z\s]*$/; // Only allow alphabets and spaces
              if (regex.test(e.target.value)) {
                setSubscriberDetails({
                  ...subscriberDetails,
                  name: e.target.value,
                });
              }
            }}
          />
          <TextField
            label={
              <span>
                Email<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.email || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                email: e.target.value,
              })
            }
          />
          <TextField
            label={
              <span>
                Phone<span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.phoneNumber || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            inputProps={{ maxLength: 10 }} // Limit input length to 10 characters
            onChange={(e) => {
              const regex = /^[0-9]*$/; // Only allow digits
              if (regex.test(e.target.value)) {
                setSubscriberDetails({
                  ...subscriberDetails,
                  phoneNumber: e.target.value,
                });
              }
            }}
          />
          <TextField
            label={
              <span>
                Date of Birth<span style={{ color: "red" }}> *</span>
              </span>
            }
            type="date" // Make sure the type is set to date
            value={subscriberDetails.dateOfBirth || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            error={Boolean(dateOfBirthError)} // Show error when necessary
            helperText={dateOfBirthError} // Display error message
            onChange={(e) => {
              const inputDate = e.target.value;
              // Update the date as the user types without validating yet
              setSubscriberDetails({
                ...subscriberDetails,
                dateOfBirth: inputDate,
              });
            }}
            onBlur={() => {
              // Validate the age on blur (when leaving the input)
              const birthDate = new Date(subscriberDetails.dateOfBirth);

              // Check if the date is valid (date should be complete and correct)
              if (isNaN(birthDate.getTime())) {
                setDateOfBirthError("Please enter a valid date");
                return;
              }

              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const monthDifference = today.getMonth() - birthDate.getMonth();

              if (
                monthDifference < 0 ||
                (monthDifference === 0 && today.getDate() < birthDate.getDate())
              ) {
                age--;
              }

              // Display error if age is less than 18
              if (age < 18) {
                setDateOfBirthError("You must be at least 18 years old");
              } else {
                setDateOfBirthError("");
              }
            }}
            InputLabelProps={{
              shrink: true, // This ensures the label stays in place for date inputs
            }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Gender</InputLabel>
            <Select
              value={subscriberDetails.gender || ""}
              onChange={(e) =>
                setSubscriberDetails({
                  ...subscriberDetails,
                  gender: e.target.value,
                })
              }
              variant="outlined"
              label="Gender"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Address"
            value={subscriberDetails.address || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                address: e.target.value,
              })
            }
          />
          <TextField
            label="Role"
            value={subscriberDetails.role || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                role: e.target.value,
              })
            }
          />

          <TextField
            label="Street Name"
            value={subscriberDetails.streetName || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                streetName: e.target.value,
              })
            }
          />
          <TextField
            label="City"
            value={subscriberDetails.city || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                city: e.target.value,
              })
            }
          />
          <TextField
            label="State"
            value={subscriberDetails.state || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                state: e.target.value,
              })
            }
          />
          <TextField
            label="Country"
            value={subscriberDetails.country || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                country: e.target.value,
              })
            }
          />
          <TextField
            label="Pincode"
            value={subscriberDetails.pincode || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                pincode: e.target.value,
              })
            }
          />
          {/* Security Questions */}
          <Typography gutterBottom sx={{ mt: 1 }}>
            Security Questions<span style={{ color: "red" }}> *</span>
          </Typography>
          {/* // Input for Security Answer 1 (Correcting field binding) */}
          <TextField
            label={
              <span>
                Your first college name?
                <span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.securityAnswer1 || ""} // Bind to securityAnswer1
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer1: e.target.value, // Update the correct state field
              })
            }
          />
          {/* // Input for Security Answer 2 (Correcting field binding) */}
          <TextField
            label={
              <span>
                Your favourite sport?
                <span style={{ color: "red" }}> *</span>
              </span>
            }
            value={subscriberDetails.securityAnswer2 || ""} // Bind to securityAnswer2
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer2: e.target.value, // Update the correct state field
              })
            }
          />
          <TextField
            label="Password"
            value={subscriberDetails.password || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                password: e.target.value,
              })
            }
          />
          {/* <TextField
            label="Security Answer 1"
            value={subscriberDetails.securityAnswer1 || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer1: e.target.value,
              })
            }
          />
          <TextField
            label="Security Answer 2"
            value={subscriberDetails.securityAnswer2 || ""}
            fullWidth
            margin="dense"
            variant="outlined"
            onChange={(e) =>
              setSubscriberDetails({
                ...subscriberDetails,
                securityAnswer2: e.target.value,
              })
            }
          /> */}
          {/* Enrollment Status Dropdown */}
          {/* <FormControl fullWidth margin="dense">
            <InputLabel>Enrollment Status</InputLabel>
            <Select
              value={subscriberDetails.enrollmentStatus || "Inactive"}
              onChange={(e) =>
                setSubscriberDetails({
                  ...subscriberDetails,
                  enrollmentStatus: e.target.value,
                })
              }
              variant="outlined"
              label="Enrollment Status"
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button
            onClick={addSubscriber}
            color="primary"
            variant="contained"
            sx={{
              backgroundColor: "#e9a132",
              "&:hover": { backgroundColor: "#c78926" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
        {/* Progress bar and countdown */}
        {isButtonDisabled && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress} // Update the progress bar
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please wait ${timer} seconds before sending another verification
              link.
            </Typography>
          </Box>
        )}
      </Dialog>

      {/* History Dialog for Not Enrolled Subscribers */}
      <Dialog open={openHistoryDialog} onClose={handleClose}>
        <DialogTitle>Enrollment History</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: "100%" }}>
            <DataGridPro
              rows={historyRows}
              columns={historyColumns}
              pageSize={5}
              getRowId={(row) => row.id}
              className="animated-input"
              sx={{
                boxShadow: 5,
                border: "none",
                "& .MuiDataGrid-cell:hover": {
                  color: "primary.main",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  color: "white !important",
                  fontWeight: "bold !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#e9a132 !important",
                  borderBottom: "white !important",
                  alignContent: "center",
                  alignItems: "center",
                  display: "flex",
                  height: "100%",
                },
                "& .MuiDataGrid-columnSeparator": {
                  visibility: "hidden",
                },
                "& .MuiDataGrid-toolbarContainer": {
                  color: "white !important",
                  justifyContent: "space-between",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "none",
                  color: "white !important",
                  fontWeight: "bold !important",
                  backgroundColor: "#e9a132 !important",
                },
                "& .MuiTablePagination-root": {
                  color: "white !important",
                },
                "& .MuiIconButton-root": {
                  color: "black !important",
                },
                "& .MuiDataGrid-selectedRowCount": {
                  color: "white !important",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center"}}
        sx={{ zIndex: 1500 }}  // Ensure it is above the dialog
        
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageSubscriber;
